import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, Alert, IconButton, MenuItem, Chip } from '@mui/material';
import { CameraAlt, Stop, Keyboard, Cameraswitch } from '@mui/icons-material';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import { GS } from '../../utils/gs1Parser';

export type ScanKind = '1d' | '2d';

interface ScannerViewProps {
  /** Fired on every successful decode. `kind` distinguishes 2D (GS1 DataMatrix) from 1D barcodes. */
  onDecode: (text: string, kind: ScanKind) => void;
  /** Optional hint shown above the camera (e.g. "Scan the 1D barcode first"). */
  prompt?: string;
  disabled?: boolean;
}

const TWO_D_FORMATS = new Set([BarcodeFormat.DATA_MATRIX, BarcodeFormat.QR_CODE, BarcodeFormat.AZTEC, BarcodeFormat.PDF_417]);

function makeReader(): BrowserMultiFormatReader {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.QR_CODE,
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.ITF,
  ]);
  hints.set(DecodeHintType.TRY_HARDER, true);
  return new BrowserMultiFormatReader(hints);
}

/**
 * Camera scanner for GS1 DataMatrix (2D) + EAN/Code128 (1D) with a manual-text
 * fallback that accepts either the parentheses form or the raw FNC1/GS form.
 * Uses ZXing-js, which decodes DataMatrix (a QR-only library would not).
 */
export default function ScannerView({ onDecode, prompt, disabled }: ScannerViewProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);
  const [lastDecode, setLastDecode] = useState<{ text: string; kind: ScanKind } | null>(null);
  const lastEmitRef = useRef<{ text: string; at: number } | null>(null);

  const stop = useCallback(() => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    // IScannerControls.stop() ends the decode loop but may leave the camera
    // stream (and indicator light) live — release the tracks explicitly.
    const video = videoRef.current;
    if (video && video.srcObject) {
      (video.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
      video.srcObject = null;
    }
    setScanning(false);
  }, []);

  // Tear down the camera on unmount.
  useEffect(() => () => stop(), [stop]);

  const emit = useCallback(
    (text: string, kind: ScanKind) => {
      // Debounce identical reads (ZXing fires continuously) for ~1.2s.
      const now = Date.now();
      const prev = lastEmitRef.current;
      if (prev && prev.text === text && now - prev.at < 1200) return;
      lastEmitRef.current = { text, at: now };
      setLastDecode({ text, kind });
      onDecode(text, kind);
    },
    [onDecode]
  );

  const start = useCallback(
    async (preferDeviceId?: string) => {
      setError(null);
      try {
        if (!readerRef.current) readerRef.current = makeReader();
        const reader = readerRef.current;
        // Enumerate cameras (best-effort; some browsers require an active stream first).
        try {
          const cams = await BrowserMultiFormatReader.listVideoInputDevices();
          setDevices(cams);
          if (!preferDeviceId && cams.length) {
            const back = cams.find((c) => /back|rear|environment/i.test(c.label));
            preferDeviceId = (back ?? cams[cams.length - 1]).deviceId;
          }
        } catch {
          /* enumeration can fail before permission; decodeFromVideoDevice still works */
        }
        setDeviceId(preferDeviceId);
        controlsRef.current = await reader.decodeFromVideoDevice(
          preferDeviceId,
          videoRef.current as HTMLVideoElement,
          (result) => {
            if (!result) return;
            const fmt = result.getBarcodeFormat();
            emit(result.getText(), TWO_D_FORMATS.has(fmt) ? '2d' : '1d');
          }
        );
        setScanning(true);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Camera unavailable';
        setError(`${msg}. Use manual entry below.`);
        setShowManual(true);
        setScanning(false);
      }
    },
    [emit]
  );

  const switchCamera = useCallback(() => {
    if (devices.length < 2) return;
    const idx = devices.findIndex((d) => d.deviceId === deviceId);
    const next = devices[(idx + 1) % devices.length];
    stop();
    void start(next.deviceId);
  }, [devices, deviceId, start, stop]);

  const submitManual = useCallback(() => {
    const text = manual.trim();
    if (!text) return;
    // A code containing GS1 AIs / parentheses / FNC1 is treated as 2D DataMatrix;
    // a plain numeric string is treated as a 1D barcode.
    const looksGs1 = text.includes(GS) || /[()]/.test(text) || /^01\d{14}/.test(text);
    emit(text, looksGs1 ? '2d' : '1d');
    setManual('');
  }, [manual, emit]);

  const displayDecode = lastDecode ? lastDecode.text.split(GS).join('·') : '';

  return (
    <Box>
      {prompt && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {prompt}
        </Typography>
      )}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'black',
          borderRadius: 1,
          overflow: 'hidden',
          aspectRatio: '4 / 3',
          display: scanning ? 'block' : 'none',
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: '18% 12%',
            border: '2px solid rgba(255,255,255,0.85)',
            borderRadius: 1,
            boxShadow: '0 0 0 100vmax rgba(0,0,0,0.25)',
          }}
        />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: scanning ? 1 : 0, flexWrap: 'wrap', gap: 1 }}>
        {!scanning ? (
          <Button variant="contained" startIcon={<CameraAlt />} onClick={() => start(deviceId)} disabled={disabled}>
            Start camera
          </Button>
        ) : (
          <Button variant="outlined" color="error" startIcon={<Stop />} onClick={stop}>
            Stop
          </Button>
        )}
        {scanning && devices.length > 1 && (
          <IconButton onClick={switchCamera} aria-label="Switch camera" title="Switch camera">
            <Cameraswitch />
          </IconButton>
        )}
        <Button variant="text" startIcon={<Keyboard />} onClick={() => setShowManual((s) => !s)}>
          Manual entry
        </Button>
        {devices.length > 1 && scanning && (
          <TextField
            select
            size="small"
            label="Camera"
            value={deviceId ?? ''}
            onChange={(e) => {
              stop();
              void start(e.target.value);
            }}
            sx={{ minWidth: 160 }}
          >
            {devices.map((d, i) => (
              <MenuItem key={d.deviceId} value={d.deviceId}>
                {d.label || `Camera ${i + 1}`}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {lastDecode && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Chip
            size="small"
            color={lastDecode.kind === '2d' ? 'secondary' : 'default'}
            label={lastDecode.kind === '2d' ? 'DataMatrix' : '1D barcode'}
          />
          <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {displayDecode}
          </Typography>
        </Stack>
      )}

      {showManual && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="flex-start">
          <TextField
            fullWidth
            size="small"
            label="Manual code entry"
            placeholder="(01)06285128023429(10)CB0423(17)270227(21)... or a 1D barcode"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitManual();
            }}
            multiline
            maxRows={3}
          />
          <Button variant="contained" onClick={submitManual} sx={{ mt: 0.5 }}>
            Add
          </Button>
        </Stack>
      )}
    </Box>
  );
}
